import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Mic, MicOff, ChevronLeft, ChevronRight, Check, Volume2, Pencil } from 'lucide-react';
import * as SpeechSDK from 'microsoft-cognitiveservices-speech-sdk';
import {
  fetchInterviewerFeedbackSession,
  saveInterviewerFeedbackSession,
  submitInterviewerFeedback,
} from '../api/interviewerFeedbackApi';
import {
  FEEDBACK_HOW_IT_WORKS_TIPS,
  getFeedbackVoiceStatusMessage,
} from '../utils/interviewerFeedbackInstructions';
import {
  clearConditionalFeedbackAnswers,
  feedbackQuestionUsesVoiceInput,
  filterVisibleFeedbackQuestions,
  formatFeedbackDisplayName,
  mergeFeedbackStepAnswer,
  normalizeFeedbackQuestionType,
  parseSkillMatrixAnswer,
  resolveFeedbackStepDraftValues,
  serializeSkillMatrixAnswer,
  validateAllFeedbackAnswers,
  validateFeedbackStep,
} from '../utils/interviewerFeedbackUtils';
import {
  playInterviewerFeedbackTts,
  stopInterviewerFeedbackTts,
} from '../utils/liveInterviewFeedbackTts';

const AZURE_SPEECH_KEY = (
  process.env.REACT_APP_COG_SERVICE_KEY ||
  process.env.REACT_APP_AZURE_SPEECH_KEY ||
  ''
).trim();
const AZURE_SPEECH_REGION = (
  process.env.REACT_APP_COG_SERVICE_LOCATION ||
  process.env.REACT_APP_AZURE_SPEECH_REGION ||
  ''
).trim();
const AZURE_SPEECH_LANGUAGE = (
  process.env.REACT_APP_COG_SERVICE_LANGUAGE ||
  process.env.REACT_APP_AZURE_SPEECH_LANGUAGE ||
  'en-IN'
).trim();

const supportsSpeech = Boolean(AZURE_SPEECH_KEY && AZURE_SPEECH_REGION);

const supportsTts = typeof window !== 'undefined' && 'speechSynthesis' in window;
const POST_QUESTION_LISTEN_DELAY_MS = 2500;

function normalizeWhitespace(text) {
  return String(text || '').replace(/\s+/g, ' ').trim();
}

function normalizeCompareText(text) {
  return normalizeWhitespace(text)
    .toLowerCase()
    .replace(/[^\w\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** True when STT output is mostly the spoken question leaking back through the mic. */
function isLikelyPromptEcho(text, prompt, label) {
  const cleaned = normalizeCompareText(text);
  const promptText = normalizeCompareText(prompt);
  const labelText = normalizeCompareText(label);
  if (!cleaned) return true;
  if (promptText && (cleaned === promptText || cleaned.startsWith(`${promptText} `))) {
    return true;
  }
  if (labelText && (cleaned === labelText || cleaned.startsWith(`${labelText} `))) {
    return true;
  }
  const reference = promptText || labelText;
  if (!reference) return false;
  const referenceWords = new Set(reference.split(' ').filter((word) => word.length > 2));
  const answerWords = cleaned.split(' ').filter(Boolean);
  if (!answerWords.length) return true;
  const overlap = answerWords.filter((word) => referenceWords.has(word)).length;
  return overlap / answerWords.length >= 0.85 && answerWords.length <= referenceWords.size + 2;
}

/** Remove only leading AI question echo — do not strip valid answer words globally. */
function stripPromptFromAnswer(text, prompt, label) {
  let cleaned = normalizeWhitespace(text);
  const phrases = [prompt, label].map(normalizeWhitespace).filter(Boolean);
  if (!cleaned || !phrases.length) return cleaned;

  for (const phrase of phrases) {
    const lowerCleaned = cleaned.toLowerCase();
    const lowerPhrase = phrase.toLowerCase();
    if (lowerCleaned.startsWith(lowerPhrase)) {
      cleaned = cleaned.slice(phrase.length).replace(/^[\s:,-]+/, '').trim();
    }
  }

  if (isLikelyPromptEcho(cleaned, prompt, label)) {
    return '';
  }

  return cleaned;
}

/** Collapse stutter like "my name is my name is pallavi". */
function dedupeStutter(text) {
  let current = normalizeWhitespace(text);
  if (!current) return '';

  for (let pass = 0; pass < 8; pass += 1) {
    const next = current.replace(/\b(\w+(?:\s+\w+){0,6})\s+\1\b/gi, '$1');
    if (next === current) break;
    current = next;
  }

  return current;
}

function tokenizeTranscriptWords(text) {
  return normalizeWhitespace(text)
    .split(' ')
    .filter(Boolean)
    .map((word) => ({
      raw: word,
      compare: word.toLowerCase().replace(/[^\w]/g, ''),
    }));
}

/**
 * Merge two transcript fragments without repeating overlapping words.
 * Browser speech APIs often resend the same phrase with a few extra words.
 */
export function mergeTranscriptText(baseText, incomingText) {
  const base = normalizeWhitespace(baseText);
  const incoming = normalizeWhitespace(incomingText);

  if (!base) return incoming;
  if (!incoming) return base;

  const baseCompare = normalizeCompareText(base);
  const incomingCompare = normalizeCompareText(incoming);

  if (!baseCompare) return incoming;
  if (!incomingCompare) return base;

  if (baseCompare === incomingCompare) {
    return incoming.length >= base.length ? incoming : base;
  }
  if (baseCompare.includes(incomingCompare)) {
    return base;
  }
  if (incomingCompare.includes(baseCompare)) {
    return incoming;
  }

  const baseWords = tokenizeTranscriptWords(base);
  const incomingWords = tokenizeTranscriptWords(incoming);
  const maxOverlap = Math.min(baseWords.length, incomingWords.length);

  for (let size = maxOverlap; size >= 2; size -= 1) {
    const left = baseWords.slice(-size).map((word) => word.compare).join(' ');
    const right = incomingWords.slice(0, size).map((word) => word.compare).join(' ');
    if (!left || left !== right) continue;

    return normalizeWhitespace(
      `${baseWords.map((word) => word.raw).join(' ')} ${incomingWords
        .slice(size)
        .map((word) => word.raw)
        .join(' ')}`
    );
  }

  return normalizeWhitespace(`${base} ${incoming}`);
}

export function buildRecognitionTranscript(results) {
  let finalText = '';
  let interimText = '';

  for (const result of Array.from(results || [])) {
    const part = normalizeWhitespace(result?.[0]?.transcript || '');
    if (!part) continue;
    if (result.isFinal) {
      finalText = mergeTranscriptText(finalText, part);
    } else {
      interimText = mergeTranscriptText(interimText, part);
    }
  }

  return {
    finalText,
    interimText,
    displayText: mergeTranscriptText(finalText, interimText),
  };
}

function normalizeSessionAnswers(rawSession) {
  const questionDefs = Array.isArray(rawSession?.questions) ? rawSession.questions : [];
  const savedAnswers = Array.isArray(rawSession?.answers) ? rawSession.answers : [];

  if (questionDefs.length) {
    const answersById = new Map(savedAnswers.map((item) => [item.id, item]));
    return questionDefs.map((question) => {
      const saved = answersById.get(question.id) || {};
      return {
        id: question.id,
        label: question.label || saved.label || question.prompt,
        question: question.prompt || question.question || saved.question || question.label,
        prompt: question.prompt || question.question || saved.question || question.label,
        answer: saved.answer || '',
        required: question.required === true || question.id === 'interviewer_name',
        type: question.type || saved.type || 'text',
        options: Array.isArray(question.options) ? question.options : saved.options || [],
        skills: Array.isArray(question.skills) ? question.skills : saved.skills || [],
        show_when: question.show_when || saved.show_when || null,
      };
    });
  }

  if (!savedAnswers.length) {
    return [];
  }

  return savedAnswers.map((item) => ({
    id: item.id,
    label: item.label || item.question,
    question: item.question || item.prompt || item.label,
    prompt: item.question || item.prompt || item.label,
    answer: item.answer || '',
    required: item.required === true || item.id === 'interviewer_name',
    type: item.type || 'text',
    options: item.options || [],
    skills: item.skills || [],
    show_when: item.show_when || null,
  }));
}

export default function InterviewerFeedbackWizard({
  open,
  token,
  actorEmail,
  variant = 'modal',
  onClose,
  onSubmitted,
}) {
  const isStandalone = variant === 'standalone';
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [stepError, setStepError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [session, setSession] = useState(null);
  const [stepIndex, setStepIndex] = useState(0);
  const [draftAnswer, setDraftAnswer] = useState('');
  const [listening, setListening] = useState(false);
  const [speakingQuestion, setSpeakingQuestion] = useState(false);
  const [voicePaused, setVoicePaused] = useState(false);
  const [questionSpeechError, setQuestionSpeechError] = useState('');
  const [questionReplayCount, setQuestionReplayCount] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [draftSaveWarning, setDraftSaveWarning] = useState('');
  const [typingMode, setTypingMode] = useState(false);
  const [skillDraft, setSkillDraft] = useState({});

  const allQuestions = useMemo(() => normalizeSessionAnswers(session), [session]);
  const visibleQuestions = useMemo(
    () => filterVisibleFeedbackQuestions(allQuestions, allQuestions),
    [allQuestions]
  );

  const current = visibleQuestions[stepIndex] || null;
  const totalSteps = visibleQuestions.length;
  const currentQuestionType = normalizeFeedbackQuestionType(current);
  const usesVoiceInput = feedbackQuestionUsesVoiceInput(current);
  const displayError = stepError || submitError;
  const voiceAlwaysOn = supportsSpeech && !voicePaused && usesVoiceInput;

  const recognitionRef = useRef(null);
  const recognitionActiveRef = useRef(false);
  const recognitionStartingRef = useRef(false);
  const recognitionStoppingRef = useRef(false);
  const voiceBaselineRef = useRef('');
  const sessionBaselineRef = useRef('');
  const sessionFinalRef = useRef('');
  const lastTranscriptRef = useRef('');
  const activeQuestionIdRef = useRef('');
  const currentPromptRef = useRef('');
  const currentLabelRef = useRef('');
  const intentionalStopRef = useRef(false);
  const isSpeakingRef = useRef(false);
  const restartTimerRef = useRef(null);
  const speechStartTimerRef = useRef(null);
  const speechRetryTimerRef = useRef(null);
  const listenAfterQuestionTimerRef = useRef(null);
  const ignoreEchoUntilRef = useRef(0);
  const openRef = useRef(open);
  const voicePausedRef = useRef(voicePaused);
  const typingModeRef = useRef(false);
  const textareaRef = useRef(null);
  const startListeningRef = useRef(null);
  const resumePendingRef = useRef(false);

  useEffect(() => {
    if (stepIndex >= visibleQuestions.length && visibleQuestions.length > 0) {
      setStepIndex(visibleQuestions.length - 1);
    }
  }, [stepIndex, visibleQuestions.length]);

  useEffect(() => {
    if (!open || !supportsTts) return undefined;
    const preloadVoices = () => {
      window.speechSynthesis?.getVoices?.();
    };
    preloadVoices();
    window.speechSynthesis?.addEventListener?.('voiceschanged', preloadVoices);
    return () => {
      window.speechSynthesis?.removeEventListener?.('voiceschanged', preloadVoices);
    };
  }, [open]);

  useEffect(() => {
    openRef.current = open;
  }, [open]);

  useEffect(() => {
    voicePausedRef.current = voicePaused;
  }, [voicePaused]);

  useEffect(() => {
    typingModeRef.current = typingMode;
  }, [typingMode]);

  const clearSpeechTimers = useCallback(() => {
    if (speechStartTimerRef.current) {
      clearTimeout(speechStartTimerRef.current);
      speechStartTimerRef.current = null;
    }
    if (speechRetryTimerRef.current) {
      clearTimeout(speechRetryTimerRef.current);
      speechRetryTimerRef.current = null;
    }
    if (listenAfterQuestionTimerRef.current) {
      clearTimeout(listenAfterQuestionTimerRef.current);
      listenAfterQuestionTimerRef.current = null;
    }
  }, []);

  const syncDraftFromSession = useCallback((index, list) => {
    const item = list[index];
    const value = item?.answer || '';
    setDraftAnswer(value);
    voiceBaselineRef.current = value;
    sessionBaselineRef.current = value;
    sessionFinalRef.current = '';
    lastTranscriptRef.current = value;
  }, []);

  const loadSession = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setLoadError('');
    setStepError('');
    setSubmitError('');
    setVoicePaused(false);
    voicePausedRef.current = false;
    setTypingMode(false);
    typingModeRef.current = false;
    resumePendingRef.current = false;
    try {
      const data = await fetchInterviewerFeedbackSession(token);
      const nextSession = data.session || data;
      const normalized = normalizeSessionAnswers(nextSession);
      const visible = filterVisibleFeedbackQuestions(normalized, normalized);
      setSession({ ...nextSession, answers: normalized });
      setStepIndex(0);
      syncDraftFromSession(0, visible);
    } catch (err) {
      setLoadError(err.message || 'Failed to load feedback session.');
    } finally {
      setLoading(false);
    }
  }, [token, syncDraftFromSession]);

  useEffect(() => {
    if (open && token) {
      loadSession();
    }
  }, [open, token, loadSession]);

  useEffect(() => {
    if (!current) return;
    activeQuestionIdRef.current = current.id;
    currentPromptRef.current = current.prompt || '';
    currentLabelRef.current = current.label || '';
    const value = current.answer || '';
    if (normalizeFeedbackQuestionType(current) === 'skill_matrix') {
      setSkillDraft(parseSkillMatrixAnswer(value));
    } else {
      setSkillDraft({});
    }
    setDraftAnswer(value);
    voiceBaselineRef.current = value;
    sessionBaselineRef.current = value;
    sessionFinalRef.current = '';
    lastTranscriptRef.current = value;
    setTypingMode(false);
    typingModeRef.current = false;
    resumePendingRef.current = false;
  }, [stepIndex, current?.id, current?.prompt, current?.label, current?.type]);

  const commitDraftToSession = useCallback((value) => {
    const questionId = activeQuestionIdRef.current;
    if (!questionId) return;
    const nextValue = String(value ?? '');
    setSession((prev) => {
      const answers = (prev?.answers || []).map((item) =>
        item.id === questionId ? { ...item, answer: nextValue } : item
      );
      return { ...prev, answers };
    });
    return nextValue;
  }, []);

  const applyAnswer = useCallback(
    (value, { persist = false } = {}) => {
      const cleaned = dedupeStutter(
        stripPromptFromAnswer(value, currentPromptRef.current, currentLabelRef.current)
      );
      setDraftAnswer(cleaned);
      voiceBaselineRef.current = cleaned;
      commitDraftToSession(cleaned);
      if (persist) {
        setSession((prev) => {
          const answers = (prev?.answers || []).map((item) =>
            item.id === activeQuestionIdRef.current
              ? { ...item, answer: cleaned }
              : item
          );
          persistDraft(answers);
          return { ...prev, answers };
        });
      }
      return cleaned;
    },
    [commitDraftToSession]
  );

  const handleDraftChange = (value) => {
    if (!typingModeRef.current && supportsSpeech) {
      typingModeRef.current = true;
      setTypingMode(true);
      resumePendingRef.current = false;
      stopListening(true);
      setVoicePaused(true);
      voicePausedRef.current = true;
    }
    setDraftAnswer(value);
    voiceBaselineRef.current = value;
    lastTranscriptRef.current = value;
    commitDraftToSession(value);
    setStepError('');
    setSubmitError('');
  };

  const persistDraft = async (answers) => {
    if (!token) return;
    try {
      await saveInterviewerFeedbackSession(token, answers);
      setDraftSaveWarning('');
    } catch (err) {
      console.warn('[InterviewerFeedback] draft save failed', err);
      setDraftSaveWarning('Could not save draft right now. Your answers are still on this screen.');
    }
  };

  const createAzureRecognizer = useCallback(() => {
    if (!supportsSpeech) return null;
    try {
      const speechConfig = SpeechSDK.SpeechConfig.fromSubscription(
        AZURE_SPEECH_KEY,
        AZURE_SPEECH_REGION
      );
      speechConfig.speechRecognitionLanguage = AZURE_SPEECH_LANGUAGE || 'en-IN';
      const audioConfig = SpeechSDK.AudioConfig.fromDefaultMicrophoneInput();
      return new SpeechSDK.SpeechRecognizer(speechConfig, audioConfig);
    } catch (err) {
      console.warn('[InterviewerFeedback] Azure recognizer init failed', err);
      setStepError('Azure speech could not be initialized. You can type your answer instead.');
      return null;
    }
  }, []);

  const disposeRecognition = useCallback((recognizer) => {
    if (!recognizer) return;
    try {
      recognizer.recognizing = undefined;
      recognizer.recognized = undefined;
      recognizer.canceled = undefined;
      recognizer.sessionStopped = undefined;
    } catch {
      // Best-effort cleanup only.
    }
    if (recognitionRef.current === recognizer) {
      recognitionRef.current = null;
    }
    recognitionActiveRef.current = false;
    recognitionStartingRef.current = false;
    recognitionStoppingRef.current = false;

    setTimeout(() => {
      try {
        recognizer.close();
      } catch {
        // Best-effort cleanup only.
      }
    }, 0);
  }, []);

  const finalizeRecognitionDraft = useCallback(
    (persist = false) => {
      const candidateValue =
        normalizeWhitespace(lastTranscriptRef.current) ||
        normalizeWhitespace(
          mergeTranscriptText(sessionBaselineRef.current, sessionFinalRef.current)
        ) ||
        normalizeWhitespace(voiceBaselineRef.current);

      const finalValue = applyAnswer(candidateValue, { persist });
      lastTranscriptRef.current = finalValue;
      sessionBaselineRef.current = finalValue;
      sessionFinalRef.current = '';
      return finalValue;
    },
    [applyAnswer]
  );

  const clearRestartTimer = () => {
    if (restartTimerRef.current) {
      clearTimeout(restartTimerRef.current);
      restartTimerRef.current = null;
    }
  };

  const resumeListeningAfterStop = useCallback(() => {
    if (!resumePendingRef.current) return false;
    resumePendingRef.current = false;
    startListeningRef.current?.();
    return true;
  }, []);

  const stopListening = useCallback((intentional = true) => {
    intentionalStopRef.current = intentional;
    clearRestartTimer();
    const recognizer = recognitionRef.current;
    if (!recognizer) {
      recognitionActiveRef.current = false;
      recognitionStartingRef.current = false;
      recognitionStoppingRef.current = false;
      setListening(false);
      return;
    }

    if (recognitionStartingRef.current && !recognitionActiveRef.current) {
      recognitionStoppingRef.current = true;
      setListening(false);
      return;
    }

    if (!recognitionActiveRef.current) {
      disposeRecognition(recognizer);
      setListening(false);
      resumeListeningAfterStop();
      return;
    }

    try {
      recognitionStoppingRef.current = true;
      recognizer.stopContinuousRecognitionAsync(
        () => {
          recognitionActiveRef.current = false;
          setListening(false);
          if (recognitionRef.current === recognizer) {
            recognizer.sessionStopped?.();
            if (recognitionRef.current === recognizer) {
              disposeRecognition(recognizer);
              resumeListeningAfterStop();
            }
          }
        },
        (err) => {
          console.warn('[InterviewerFeedback] Azure stop failed', err);
          disposeRecognition(recognizer);
          setListening(false);
          resumeListeningAfterStop();
        }
      );
    } catch (err) {
      console.warn('[InterviewerFeedback] Azure stop threw', err);
      disposeRecognition(recognizer);
      setListening(false);
      resumeListeningAfterStop();
    }
  }, [disposeRecognition, resumeListeningAfterStop]);

  const scheduleRestartListening = useCallback(() => {
    clearRestartTimer();
    if (
      intentionalStopRef.current ||
      voicePausedRef.current ||
      typingModeRef.current ||
      isSpeakingRef.current ||
      !openRef.current ||
      !supportsSpeech
    ) {
      return;
    }
    restartTimerRef.current = setTimeout(() => {
      restartTimerRef.current = null;
      if (
        !intentionalStopRef.current &&
        !voicePausedRef.current &&
        !typingModeRef.current &&
        !isSpeakingRef.current &&
        openRef.current
      ) {
        startListeningRef.current?.();
      }
    }, 900);
  }, []);

  const startListening = useCallback(() => {
    if (
      !supportsSpeech ||
      voicePausedRef.current ||
      typingModeRef.current ||
      isSpeakingRef.current ||
      !openRef.current
    ) {
      return;
    }
    if (!activeQuestionIdRef.current) return;
    if (recognitionRef.current || recognitionActiveRef.current) return;

    intentionalStopRef.current = false;
    setStepError('');

    const recognition = createAzureRecognizer();
    if (!recognition) return;

    recognitionRef.current = recognition;
    recognitionActiveRef.current = false;
    recognitionStartingRef.current = true;
    const committedBaseline = normalizeWhitespace(voiceBaselineRef.current || draftAnswer);
    voiceBaselineRef.current = committedBaseline;
    sessionBaselineRef.current = committedBaseline;
    sessionFinalRef.current = '';
    lastTranscriptRef.current = committedBaseline;
    let recognitionSettled = false;

    const settleRecognition = ({
      intentional = false,
      errorMessage = '',
      pauseVoice = false,
    } = {}) => {
      if (recognitionSettled) return;
      recognitionSettled = true;
      recognitionStartingRef.current = false;
      recognitionActiveRef.current = false;
      setListening(false);
      disposeRecognition(recognition);

      const hasTranscript =
        normalizeWhitespace(lastTranscriptRef.current) !==
          normalizeWhitespace(sessionBaselineRef.current) ||
        sessionFinalRef.current.trim().length > 0;
      if (hasTranscript) {
        finalizeRecognitionDraft(true);
      }

      if (intentional) {
        intentionalStopRef.current = false;
        resumeListeningAfterStop();
        return;
      }

      if (errorMessage) {
        setStepError(errorMessage);
      }

      if (pauseVoice) {
        setVoicePaused(true);
        voicePausedRef.current = true;
        return;
      }

      if (resumeListeningAfterStop()) {
        return;
      }

      if (!voicePausedRef.current && !isSpeakingRef.current) {
        scheduleRestartListening();
      }
    };

    recognition.recognizing = (_sender, event) => {
      if (recognitionRef.current !== recognition || recognitionSettled) return;
      if (isSpeakingRef.current || typingModeRef.current) return;
      if (Date.now() < ignoreEchoUntilRef.current) return;
      const interimText = normalizeWhitespace(event?.result?.text || '');
      if (!interimText) return;
      const rawDisplay = mergeTranscriptText(
        sessionBaselineRef.current,
        mergeTranscriptText(sessionFinalRef.current, interimText)
      );
      if (isLikelyPromptEcho(rawDisplay, currentPromptRef.current, currentLabelRef.current)) {
        return;
      }
      const display = dedupeStutter(
        stripPromptFromAnswer(rawDisplay, currentPromptRef.current, currentLabelRef.current)
      );
      if (!display && !normalizeWhitespace(sessionBaselineRef.current)) {
        return;
      }
      if (normalizeWhitespace(display) === normalizeWhitespace(lastTranscriptRef.current)) {
        return;
      }
      lastTranscriptRef.current = display;
      setDraftAnswer(display);
      commitDraftToSession(display);
    };

    recognition.recognized = (_sender, event) => {
      if (recognitionRef.current !== recognition || recognitionSettled) return;
      if (isSpeakingRef.current || typingModeRef.current) return;
      if (Date.now() < ignoreEchoUntilRef.current) return;

      const result = event?.result;
      if (!result) return;
      if (result.reason !== SpeechSDK.ResultReason.RecognizedSpeech || !result.text) {
        return;
      }

      sessionFinalRef.current = mergeTranscriptText(
        sessionFinalRef.current,
        normalizeWhitespace(result.text)
      );
      const rawDisplay = mergeTranscriptText(
        sessionBaselineRef.current,
        sessionFinalRef.current
      );
      if (isLikelyPromptEcho(rawDisplay, currentPromptRef.current, currentLabelRef.current)) {
        return;
      }
      const display = dedupeStutter(
        stripPromptFromAnswer(rawDisplay, currentPromptRef.current, currentLabelRef.current)
      );
      if (!display) return;
      if (normalizeWhitespace(display) === normalizeWhitespace(lastTranscriptRef.current)) {
        return;
      }
      lastTranscriptRef.current = display;
      setDraftAnswer(display);
      commitDraftToSession(display);
    };

    recognition.canceled = (_sender, event) => {
      if (recognitionSettled) return;
      const wasIntentionalStop = intentionalStopRef.current;
      const reason = event?.reason;
      const errorCode = String(event?.errorCode || '').trim();
      const errorDetails = String(event?.errorDetails || '').trim();

      if (reason === SpeechSDK.CancellationReason.Error) {
        const lowered = `${errorCode} ${errorDetails}`.toLowerCase();
        if (
          lowered.includes('permission') ||
          lowered.includes('notallowed') ||
          lowered.includes('forbidden') ||
          lowered.includes('microphone')
        ) {
          settleRecognition({
            intentional: wasIntentionalStop,
            errorMessage: 'Microphone access is blocked. Allow the mic or type your answer.',
            pauseVoice: true,
          });
          return;
        }
        settleRecognition({
          intentional: wasIntentionalStop,
          errorMessage: errorDetails || 'Azure speech recognition stopped unexpectedly.',
        });
        return;
      }
      settleRecognition({ intentional: wasIntentionalStop });
    };

    recognition.sessionStopped = () => {
      if (recognitionSettled) return;
      const wasIntentionalStop = intentionalStopRef.current;
      settleRecognition({ intentional: wasIntentionalStop });
    };

    try {
      recognition.startContinuousRecognitionAsync(
        () => {
          recognitionStartingRef.current = false;
          if (recognitionRef.current !== recognition || recognitionSettled) {
            disposeRecognition(recognition);
            return;
          }
          recognitionActiveRef.current = true;
          if (intentionalStopRef.current) {
            setListening(false);
            setTimeout(() => {
              if (recognitionRef.current === recognition && !recognitionSettled) {
                stopListening(true);
              }
            }, 0);
            return;
          }
          recognitionStoppingRef.current = false;
          resumePendingRef.current = false;
          setListening(true);
          setStepError('');
        },
        (err) => {
          console.warn('[InterviewerFeedback] Azure recognition start failed', err);
          recognitionStartingRef.current = false;
          recognitionActiveRef.current = false;
          disposeRecognition(recognition);
          setListening(false);
          setStepError('Azure speech could not start. You can type your answer instead.');
          scheduleRestartListening();
        }
      );
    } catch (err) {
      console.warn('[InterviewerFeedback] recognition start failed', err);
      recognitionStartingRef.current = false;
      recognitionActiveRef.current = false;
      disposeRecognition(recognition);
      setListening(false);
      setStepError('Azure speech could not start. You can type your answer instead.');
      scheduleRestartListening();
    }
  }, [
    commitDraftToSession,
    createAzureRecognizer,
    disposeRecognition,
    draftAnswer,
    finalizeRecognitionDraft,
    resumeListeningAfterStop,
    scheduleRestartListening,
    stopListening,
  ]);

  useEffect(() => {
    startListeningRef.current = startListening;
  }, [startListening]);

  const speakQuestionPrompt = useCallback((text, onComplete) => {
    if (!openRef.current || !text) {
      onComplete?.();
      return;
    }

    clearSpeechTimers();
    stopListening(true);
    stopInterviewerFeedbackTts();
    setQuestionSpeechError('');

    const savedAnswer = normalizeWhitespace(voiceBaselineRef.current);
    setDraftAnswer(savedAnswer);
    voiceBaselineRef.current = savedAnswer;
    sessionBaselineRef.current = savedAnswer;
    sessionFinalRef.current = '';
    lastTranscriptRef.current = savedAnswer;

    isSpeakingRef.current = true;
    setSpeakingQuestion(true);

    const finish = (didSpeak = true) => {
      clearSpeechTimers();
      ignoreEchoUntilRef.current = Date.now() + POST_QUESTION_LISTEN_DELAY_MS;
      window.setTimeout(() => {
        isSpeakingRef.current = false;
        setSpeakingQuestion(false);
        if (!didSpeak) {
          setQuestionSpeechError('Question audio did not play. Click Replay Question, then speak your answer.');
        }
        onComplete?.();
      }, 200);
    };

    void Promise.resolve(
      playInterviewerFeedbackTts({
        text,
        azureKey: AZURE_SPEECH_KEY,
        azureRegion: AZURE_SPEECH_REGION,
        language: AZURE_SPEECH_LANGUAGE,
      })
    )
      .then((didSpeak) => {
        if (!openRef.current) {
          onComplete?.();
          return;
        }
        finish(Boolean(didSpeak));
      })
      .catch((error) => {
        console.warn('[InterviewerFeedback] Question audio failed', error);
        if (!openRef.current) {
          onComplete?.();
          return;
        }
        finish(false);
      });
  }, [clearSpeechTimers, stopListening]);

  useEffect(() => {
    if (!open || loading || loadError || !current?.prompt) {
      return undefined;
    }

    setQuestionSpeechError('');
    stopListening(true);
    clearSpeechTimers();
    stopInterviewerFeedbackTts();
    isSpeakingRef.current = false;
    setSpeakingQuestion(false);

    const timer = setTimeout(() => {
      speakQuestionPrompt(current.prompt, () => {
        listenAfterQuestionTimerRef.current = window.setTimeout(() => {
          listenAfterQuestionTimerRef.current = null;
          if (!openRef.current || voicePausedRef.current || isSpeakingRef.current) return;
          if (!feedbackQuestionUsesVoiceInput(current)) return;
          startListeningRef.current?.();
        }, POST_QUESTION_LISTEN_DELAY_MS);
      });
    }, 300);

    return () => {
      clearTimeout(timer);
      clearSpeechTimers();
      stopInterviewerFeedbackTts();
      isSpeakingRef.current = false;
      setSpeakingQuestion(false);
      ignoreEchoUntilRef.current = 0;
      stopListening(true);
    };
  }, [
    open,
    loading,
    loadError,
    current?.id,
    current?.prompt,
    questionReplayCount,
    clearSpeechTimers,
    speakQuestionPrompt,
    stopListening,
  ]);

  useEffect(
    () => () => {
      stopListening(true);
      clearRestartTimer();
      clearSpeechTimers();
      stopInterviewerFeedbackTts();
    },
    [clearSpeechTimers, stopListening]
  );

  const enterTypingMode = useCallback(() => {
    if (typingModeRef.current) {
      textareaRef.current?.focus();
      return;
    }
    typingModeRef.current = true;
    setTypingMode(true);
    resumePendingRef.current = false;
    stopListening(true);
    setVoicePaused(true);
    voicePausedRef.current = true;
    setStepError('');
    textareaRef.current?.focus();
  }, [stopListening]);

  const handleResumeVoice = useCallback(() => {
    typingModeRef.current = false;
    voicePausedRef.current = false;
    intentionalStopRef.current = false;
    setTypingMode(false);
    setVoicePaused(false);
    setStepError('');
    if (recognitionActiveRef.current && !recognitionStoppingRef.current) {
      resumePendingRef.current = false;
      setListening(true);
      return;
    }
    resumePendingRef.current = true;
    if (!recognitionRef.current && !recognitionStartingRef.current) {
      resumeListeningAfterStop();
    }
  }, [resumeListeningAfterStop]);

  const toggleVoicePause = () => {
    if (voicePaused) {
      handleResumeVoice();
      return;
    }
    resumePendingRef.current = false;
    stopListening(true);
    setVoicePaused(true);
    voicePausedRef.current = true;
  };

  const clearManualVoiceModeForNavigation = () => {
    // Typing is scoped to the current answer. Do not carry its automatic
    // microphone pause into the question the interviewer navigates to.
    // An explicit Pause microphone action keeps typingModeRef false, so it
    // remains paused as the interviewer intended.
    if (!typingModeRef.current) return;
    typingModeRef.current = false;
    voicePausedRef.current = false;
    resumePendingRef.current = false;
    setTypingMode(false);
    setVoicePaused(false);
  };

  const handleReplayQuestion = useCallback(() => {
    if (!current?.prompt || speakingQuestion) return;
    setQuestionSpeechError('');
    setQuestionReplayCount((count) => count + 1);
  }, [current?.prompt, speakingQuestion]);

  const handleOptionSelect = useCallback(
    (option) => {
      const nextValue = String(option || '').trim();
      setDraftAnswer(nextValue);
      commitDraftToSession(nextValue);
      setStepError('');
      if (current?.id === 'candidate_decision') {
        setSession((prev) => ({
          ...prev,
          answers: clearConditionalFeedbackAnswers(prev?.answers || [], nextValue),
        }));
      }
    },
    [commitDraftToSession, current?.id]
  );

  const validateCurrentStep = () => {
    const error = validateFeedbackStep(current, { draftAnswer, skillDraft });
    if (error) {
      setStepError(error);
      return false;
    }
    setStepError('');
    return true;
  };

  const buildAnswersForCurrentStep = useCallback(() => {
    const { questionType, answerValue } = resolveFeedbackStepDraftValues(current, {
      draftAnswer: currentQuestionType === 'text' ? applyAnswer(draftAnswer) : draftAnswer,
      skillDraft,
    });
    let answers = mergeFeedbackStepAnswer(session?.answers || [], current, answerValue, questionType);
    if (current?.id === 'candidate_decision') {
      answers = clearConditionalFeedbackAnswers(answers, answerValue);
    }
    return answers;
  }, [
    applyAnswer,
    current,
    currentQuestionType,
    draftAnswer,
    session?.answers,
    skillDraft,
  ]);

  const goNext = async () => {
    stopListening(true);
    clearManualVoiceModeForNavigation();
    stopInterviewerFeedbackTts();
    if (!validateCurrentStep()) return;

    const answers = buildAnswersForCurrentStep();
    setSession((prev) => ({ ...prev, answers }));
    await persistDraft(answers);
    setSubmitError('');

    const nextVisible = filterVisibleFeedbackQuestions(
      normalizeSessionAnswers({ ...session, answers }),
      answers
    );
    const currentIdx = nextVisible.findIndex((item) => item.id === current?.id);
    const nextIdx = currentIdx >= 0 ? currentIdx + 1 : stepIndex + 1;
    setStepIndex(Math.min(nextIdx, Math.max(nextVisible.length - 1, 0)));
  };

  const goBack = () => {
    stopListening(true);
    clearManualVoiceModeForNavigation();
    stopInterviewerFeedbackTts();
    if (currentQuestionType === 'skill_matrix') {
      commitDraftToSession(serializeSkillMatrixAnswer(skillDraft));
    } else {
      applyAnswer(draftAnswer);
    }
    setStepError('');
    setSubmitError('');
    setStepIndex((idx) => Math.max(idx - 1, 0));
  };

  const handleSubmit = async () => {
    stopListening(true);
    stopInterviewerFeedbackTts();
    if (!validateCurrentStep()) return;

    const answers = buildAnswersForCurrentStep();
    const questionDefs = normalizeSessionAnswers({ ...session, answers });
    const validationError = validateAllFeedbackAnswers(questionDefs, answers, {
      questionId: current?.id,
      draftAnswer,
      skillDraft,
    });
    if (validationError) {
      setSubmitError(validationError);
      return;
    }

    setSubmitting(true);
    setSubmitError('');
    setSession((prev) => ({ ...prev, answers }));

    try {
      await persistDraft(answers);
      const data = await submitInterviewerFeedback(token, answers, actorEmail);
      onSubmitted?.(data.feedback || data);
    } catch (err) {
      setSubmitError(err.message || 'Failed to submit feedback.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  const showVoiceUi = supportsSpeech || supportsTts;
  const voiceStatusMessage = getFeedbackVoiceStatusMessage({
    typingMode,
    speakingQuestion,
    voiceAlwaysOn,
    listening,
    supportsTts,
    supportsSpeech,
    usesVoiceInput,
  });

  const wrapperClassName = isStandalone
    ? 'li-feedback-wizard-standalone'
    : 'li-modal-overlay li-feedback-wizard-overlay';
  const cardClassName = isStandalone
    ? 'li-feedback-choice-card li-feedback-wizard-card li-feedback-wizard-card--standalone'
    : 'li-feedback-wizard-card';

  return (
    <div
      className={wrapperClassName}
      role="dialog"
      aria-modal={isStandalone ? undefined : 'true'}
    >
      <div className={cardClassName} onClick={(e) => e.stopPropagation()}>
        <div className="li-feedback-wizard-header">
          <div>
            <p className="li-feedback-eyebrow">Interviewer feedback</p>
            <h3 className="li-feedback-title">
              {session?.candidate_name
                ? `Feedback for ${formatFeedbackDisplayName(session.candidate_name)}`
                : 'Interviewer feedback session'}
            </h3>
            <p className="li-feedback-sub">
              About {Math.max(1, Math.ceil(totalSteps * 0.7))} min · Step {stepIndex + 1} of{' '}
              {totalSteps || 1}
            </p>
          </div>
          {!isStandalone && onClose ? (
            <button type="button" className="li-feedback-close" onClick={onClose} aria-label="Close">
              ×
            </button>
          ) : null}
        </div>

        {loading ? (
          <p className="li-feedback-body">Loading feedback questions…</p>
        ) : loadError ? (
          <div className="li-feedback-load-error">
            <p className="li-modal-error" role="alert">
              {loadError}
            </p>
            <button
              type="button"
              className="li-modal-btn li-modal-btn-secondary"
              onClick={() => {
                void loadSession();
              }}
            >
              Try again
            </button>
          </div>
        ) : current ? (
          <div className="li-feedback-wizard-body">
            {displayError ? (
              <div className="li-modal-error li-feedback-inline-error" role="alert">
                {displayError}
              </div>
            ) : null}
            {draftSaveWarning ? (
              <div className="li-feedback-voice-hint" role="status">
                {draftSaveWarning}
              </div>
            ) : null}

            {showVoiceUi ? (
              <div className="li-feedback-how-it-works" aria-label="How it works">
                <p className="li-feedback-how-it-works-title">How it works</p>
                <ul className="li-feedback-how-it-works-list">
                  {FEEDBACK_HOW_IT_WORKS_TIPS.map((tip) => (
                    <li key={tip}>{tip}</li>
                  ))}
                </ul>
              </div>
            ) : null}

            <div className="li-feedback-question-card">
              {current.label && current.label !== current.prompt ? (
                <p className="li-feedback-question-label">{current.label}</p>
              ) : null}
              <p className="li-feedback-question-prompt">{current.prompt}</p>
            </div>

            {showVoiceUi ? (
              <div
                className={`li-feedback-voice-status ${
                  typingMode
                    ? 'is-typing'
                    : listening && !speakingQuestion
                      ? 'is-live'
                      : speakingQuestion
                        ? 'is-speaking'
                        : currentQuestionType === 'single_choice' ||
                            currentQuestionType === 'skill_matrix'
                          ? 'is-choice'
                          : ''
                }`}
                role="status"
                aria-live="polite"
              >
                <span className="li-feedback-voice-status-dot" aria-hidden />
                {voiceStatusMessage}
              </div>
            ) : null}
            {questionSpeechError ? (
              <div className="li-feedback-voice-hint" role="status">
                {questionSpeechError}
              </div>
            ) : null}

            {currentQuestionType === 'single_choice' ? (
              <div className="li-feedback-options" role="radiogroup" aria-label={current.prompt}>
                {(current.options || []).map((option) => {
                  const selected = draftAnswer === option;
                  return (
                    <button
                      key={option}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      className={`li-feedback-option ${selected ? 'is-selected' : ''}`}
                      onClick={() => handleOptionSelect(option)}
                    >
                      <span className="li-feedback-option-indicator" aria-hidden />
                      <span>{option}</span>
                    </button>
                  );
                })}
              </div>
            ) : null}

            {currentQuestionType === 'skill_matrix' ? (
              <div className="li-feedback-skill-grid">
                {(current.skills || []).map((skill) => (
                  <div key={skill} className="li-feedback-skill-row">
                    <p className="li-feedback-skill-name">{skill}</p>
                    <div className="li-feedback-skill-options" role="radiogroup" aria-label={skill}>
                      {(current.options || []).map((option) => {
                        const selected = skillDraft[skill] === option;
                        return (
                          <button
                            key={`${skill}-${option}`}
                            type="button"
                            role="radio"
                            aria-checked={selected}
                            className={`li-feedback-skill-option ${selected ? 'is-selected' : ''}`}
                            onClick={() => {
                              setSkillDraft((prev) => ({ ...prev, [skill]: option }));
                              setStepError('');
                            }}
                          >
                            {option}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            ) : null}

            {!usesVoiceInput && supportsTts ? (
              <div className="li-feedback-voice-row">
                <button
                  type="button"
                  className="li-feedback-voice-btn li-feedback-replay-btn"
                  onClick={handleReplayQuestion}
                  disabled={loading || speakingQuestion || !current?.prompt}
                >
                  <Volume2 size={16} />
                  Replay question
                </button>
              </div>
            ) : null}

            {usesVoiceInput ? (
              <>
                <label className="li-feedback-field-label" htmlFor="feedback-answer-input">
                  Your answer (edit if needed)
                </label>
                <textarea
                  ref={textareaRef}
                  id="feedback-answer-input"
                  className="li-feedback-textarea"
                  rows={5}
                  value={draftAnswer}
                  onChange={(e) => handleDraftChange(e.target.value)}
                  onFocus={enterTypingMode}
                  placeholder={
                    supportsSpeech
                      ? typingMode
                        ? 'Type or edit your answer here. Spaces and punctuation are supported.'
                        : 'Wait for the green dot, then speak — or tap Edit answer to type instead.'
                      : 'Type your answer here…'
                  }
                  aria-invalid={Boolean(displayError)}
                />

                <div className="li-feedback-voice-row">
                  {supportsSpeech ? (
                    <button
                      type="button"
                      className={`li-feedback-voice-btn ${voiceAlwaysOn && listening ? 'is-active' : ''}`}
                      onClick={toggleVoicePause}
                      disabled={speakingQuestion || typingMode}
                    >
                      {voiceAlwaysOn ? <Mic size={16} /> : <MicOff size={16} />}
                      {voiceAlwaysOn ? 'Pause microphone' : 'Resume microphone'}
                    </button>
                  ) : null}
                  {supportsSpeech ? (
                    <button
                      type="button"
                      className={`li-feedback-voice-btn ${typingMode ? 'is-active' : ''}`}
                      onClick={typingMode ? handleResumeVoice : enterTypingMode}
                      disabled={speakingQuestion}
                    >
                      <Pencil size={16} />
                      {typingMode ? 'Use microphone' : 'Edit answer'}
                    </button>
                  ) : null}
                  {supportsTts ? (
                    <button
                      type="button"
                      className="li-feedback-voice-btn li-feedback-replay-btn"
                      onClick={handleReplayQuestion}
                      disabled={loading || speakingQuestion || !current?.prompt}
                    >
                      <Volume2 size={16} />
                      Replay question
                    </button>
                  ) : null}
                  <span className="li-feedback-voice-hint">
                    Use headphones for best results. Review your answer before Next.
                  </span>
                </div>
              </>
            ) : null}
          </div>
        ) : (
          <p className="li-modal-error" role="alert">
            No feedback questions are available for this session.
          </p>
        )}

        <div className="li-feedback-wizard-actions">
          <button
            type="button"
            className="li-modal-btn li-modal-btn-secondary"
            onClick={goBack}
            disabled={stepIndex === 0 || loading || submitting}
          >
            <ChevronLeft size={15} /> Back
          </button>
          {stepIndex < totalSteps - 1 ? (
            <button
              type="button"
              className="li-modal-btn li-modal-btn-primary"
              onClick={goNext}
              disabled={loading || submitting || speakingQuestion}
            >
              Next <ChevronRight size={15} />
            </button>
          ) : (
            <button
              type="button"
              className="li-modal-btn li-modal-btn-primary"
              onClick={handleSubmit}
              disabled={loading || submitting}
            >
              <Check size={15} />
              {submitting ? 'Submitting…' : 'Submit feedback'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
